import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useRouter } from 'next/router'

import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { type Locale } from '@lib/language'
import { getShopifyCartIdStorageKey } from '@lib/local-storage'
import { getLinkPageUrl } from '@lib/routes'
import { type ShopInformation } from '@lib/shop'
import { ShopContext } from '@lib/shop-context'
import { emptyCart, getOrCreateCart } from './helpers'
import { useUpdateCartShippingSettings } from './hooks'
import { type Cart } from './types'

interface CartContextProps {
  cart: Cart
  isCartOpen: boolean
  isCartUpdating: boolean
  cartAddingProductIds: number[]
  isCartSubmitting: boolean
  openCartInModal?: boolean
  toggleCart: (newState: boolean) => void
  saveCart: (cart?: Cart) => void
  setIsCartUpdating: (isCartUpdating: boolean) => void
  setCartAddingProductIds: (cartAddingProductIds: number[]) => void
  setIsCartSubmitting: (isCartSubmitting: boolean) => void
}

interface CartContextProviderProps {
  site: SanitySiteFragment
  locale: Locale
  shopInformation: ShopInformation
  children: ReactNode
}

export const CartContext = createContext<CartContextProps>({
  cart: emptyCart,
  isCartOpen: false,
  isCartUpdating: false,
  cartAddingProductIds: [],
  isCartSubmitting: false,
  toggleCart: () => null,
  saveCart: () => null,
  setIsCartUpdating: () => null,
  setCartAddingProductIds: () => null,
  setIsCartSubmitting: async () => null,
})

/**
 * The cart context provider.
 */
export const CartContextProvider = ({
  site,
  locale,
  shopInformation,
  children,
}: CartContextProviderProps) => {
  const { shopifyStorefrontGraphQlClient } = useContext(ShopContext)

  const [cart, setCart] = useState<Cart>(emptyCart)
  const [openCartInModal, setOpenCartInModal] = useState(
    !site.cartSettings?.openInSeparatePage
  )
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [isCartUpdating, setIsCartUpdating] = useState(false)
  const [cartAddingProductIds, setCartAddingProductIds] = useState<number[]>([])
  const [isCartSubmitting, setIsCartSubmitting] = useState(false)

  const router = useRouter()

  const saveCart = useCallback(
    (cart?: Cart) => {
      if (!cart) {
        return
      }

      setCart(cart)

      const shopifyCartIdStorageKey = getShopifyCartIdStorageKey(locale)
      localStorage.setItem(shopifyCartIdStorageKey, cart.id)
    },
    [locale]
  )

  const toggleCart = useCallback(
    (newState: boolean) => {
      if (!openCartInModal) {
        if (newState) {
          const url = getLinkPageUrl({
            pageType: 'cartPage',
          })
          router.push(url, url, {
            locale,
          })
        }

        return
      }

      if (isCartOpen !== newState) {
        setIsCartOpen(newState)
      }
    },
    [isCartOpen, openCartInModal, locale, router]
  )

  // Load cart settings (update when switching language)
  useEffect(() => {
    setOpenCartInModal(!site.cartSettings?.openInSeparatePage)
  }, [site.cartSettings])

  // Load cart from Shopify
  useEffect(() => {
    if (
      !shopifyStorefrontGraphQlClient ||
      !shopInformation.shop ||
      shopInformation.locale !== locale
    ) {
      return
    }

    const loadCart = async () => {
      const newCart = await getOrCreateCart(
        locale,
        shopifyStorefrontGraphQlClient,
        shopInformation.cart
      )
      saveCart(newCart)
    }
    loadCart()
  }, [locale, saveCart, shopifyStorefrontGraphQlClient, shopInformation])

  useUpdateCartShippingSettings(cart, saveCart)

  return (
    <CartContext.Provider
      value={{
        cart,
        isCartOpen,
        isCartUpdating,
        cartAddingProductIds,
        isCartSubmitting,
        openCartInModal,
        toggleCart,
        saveCart,
        setIsCartUpdating,
        setCartAddingProductIds,
        setIsCartSubmitting,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}
